import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { Company, Person } from '@marstein-radar/shared';
import { inputStyle, buttonStyle, personResultStyle } from '../styles';

interface RoleWithCompany {
  role: {
    id: string;
    roleLabel: string;
    roleCategory: string;
    validFrom: string | null;
    validTo: string | null;
  };
  company: Company;
}

// E-Analytika (2026-07-18): Osoby povýšené z Nástrojov na plnohodnotnú sekciu
// v hlavnom menu — druhý vstup do analýzy väzieb popri firmách. Logika (search
// + roly osoby naprieč firmami) presunutá 1:1 z ToolsPage, kde po presune ostali
// len hromadné B2B nástroje.
export function PersonsPage() {
  const navigate = useNavigate();
  const [personQuery, setPersonQuery] = useState('');
  const [submittedPersonQuery, setSubmittedPersonQuery] = useState<string | null>(null);
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);

  const { data: personResults, isFetching: personSearchLoading } = useQuery<Person[]>({
    queryKey: ['person-search', submittedPersonQuery],
    queryFn: async () => {
      const res = await fetch(
        `/api/v1/persons/search?q=${encodeURIComponent(submittedPersonQuery ?? '')}`,
      );
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    enabled: !!submittedPersonQuery,
  });

  const { data: personCompanies, isFetching: personCompaniesLoading } = useQuery<RoleWithCompany[]>(
    {
      queryKey: ['person-companies', selectedPerson?.id],
      queryFn: async () => {
        const res = await fetch(`/api/v1/persons/${selectedPerson!.id}/companies`);
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        return res.json();
      },
      enabled: !!selectedPerson,
    },
  );

  return (
    <main className="page page-narrow">
      <div className="page-header">
        <h1>Analýza osôb</h1>
        <p>
          Kto kde pôsobí a s kým — roly osoby naprieč slovenskými firmami z RPO a RPVS. Osoby bez
          dátumu narodenia v registri sú len zhodou mena, nie potvrdenou identitou.
        </p>
      </div>

      <section>
        <div className="card">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setSelectedPerson(null);
              setSubmittedPersonQuery(personQuery.trim());
            }}
            style={{ display: 'flex', gap: '0.5rem' }}
          >
            <input
              value={personQuery}
              onChange={(e) => setPersonQuery(e.target.value)}
              placeholder="Meno a priezvisko — napr. Jana Hrivnáková"
              style={{ ...inputStyle, flex: 1, margin: 0 }}
            />
            <button type="submit" style={buttonStyle}>
              Analyzovať
            </button>
          </form>

          {personSearchLoading && (
            <p style={{ color: 'var(--slate)', fontSize: '0.85rem', marginBottom: 0 }}>
              Načítavam...
            </p>
          )}
          {personResults && personResults.length === 0 && (
            <p style={{ color: 'var(--slate)', fontSize: '0.85rem', marginBottom: 0 }}>
              Žiadna osoba nenájdená.
            </p>
          )}
          {personResults && personResults.length > 0 && !selectedPerson && (
            <ul className="list-plain" style={{ marginTop: '0.75rem' }}>
              {personResults.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedPerson(p)}
                    style={personResultStyle}
                  >
                    {p.fullName}
                    {p.birthDate ? (
                      <span className="num" style={{ color: 'var(--slate)', fontSize: '0.8rem' }}>
                        {' '}
                        · nar. {p.birthDate}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--slate)', fontSize: '0.8rem' }}>
                        {' '}
                        · identita nepotvrdená (bez dátumu narodenia)
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {selectedPerson && (
            <div style={{ marginTop: '0.75rem' }}>
              <button
                type="button"
                onClick={() => setSelectedPerson(null)}
                className="btn-link"
                style={{ marginBottom: '0.6rem' }}
              >
                ← späť na výsledky
              </button>
              <h4 style={{ margin: '0 0 0.5rem', fontSize: '1rem' }}>
                {selectedPerson.fullName}
                {selectedPerson.birthDate ? (
                  <span
                    className="num"
                    style={{ color: 'var(--slate)', fontSize: '0.8rem', fontWeight: 400 }}
                  >
                    {' '}
                    · nar. {selectedPerson.birthDate}
                  </span>
                ) : null}
              </h4>
              {personCompaniesLoading && (
                <p style={{ color: 'var(--slate)', fontSize: '0.85rem' }}>Načítavam firmy...</p>
              )}
              {personCompanies && personCompanies.length === 0 && (
                <p style={{ color: 'var(--slate)', fontSize: '0.85rem' }}>
                  Žiadne firmy nenájdené.
                </p>
              )}
              {personCompanies && personCompanies.length > 0 && (
                <ul className="list-plain">
                  {personCompanies.map(({ role, company }) => (
                    <li key={role.id}>
                      <button
                        type="button"
                        onClick={() => navigate(`/firma/${company.ico}`)}
                        style={personResultStyle}
                      >
                        {company.name ?? company.ico}
                        <span style={{ color: 'var(--slate)', fontSize: '0.8rem' }}>
                          {' '}
                          · {role.roleLabel}
                          {role.validTo ? ` (do ${role.validTo})` : ''}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
